import { Pencil, RefreshCw, Trash2 } from 'lucide-react';

import type { KebabMenuAction } from '@/_shared/components/list/KebabMenu';

/**
 * The admin-only tooltip reason for account ROW actions — Edit, Re-authorize, Delete (spec §7, §8;
 * every write is `requireAdmin` server-side). Those disable-with-a-reason (ui skill rule 10) rather
 * than hide a control for a member, and they all read this one string through
 * `buildAccountRowActions`.
 *
 * NOT every write control: the Add-account buttons on `SettingsAccounts` say "Only workspace admins
 * can add accounts" instead, because adding is not an action on a row. Worth converging, but they
 * are two different sentences today and this constant is only the row one.
 */
export const ADMIN_ONLY_REASON = 'Only workspace admins can manage accounts';

/** Shown on Edit and Re-authorize for the window between rows painting and `listDefinitions` settling. */
export const DEFINITIONS_LOADING_REASON = 'Loading account types…';

/** The row fields `buildAccountRowActions` needs — satisfied by both `list` result shapes. */
export interface AccountRowActionSubject {
  id: string;
  name: string;
  accountDefinitionName: string;
  authenticationType: string;
}

/** The definition field `buildAccountRowActions` needs — satisfied by both `listDefinitions` result shapes. */
export interface AccountRowActionDefinition {
  lookoutSupport: string;
}

/**
 * The row-action kebab-menu builder for `SettingsAccounts` (spec §7/§8/§9). It was shared
 * with `AccountPickerSheet` until that sheet was deleted: both had a near-verbatim copy of this
 * predicate, and that duplication produced a real bug in one task and a test that could not fail in
 * another, so it lives here once. The `definitionsPending` window below was the picker's
 * requirement, kept because it describes a real state the three surviving surfaces also have:
 * `SettingsAccounts`, the wizard's `AccountField`, and the activation sheet's sync-account row.
 *
 * Edit is gated on the definition's `lookoutSupport === 'form'`, NOT `authenticationType !== 'oauth'`
 * alone — a non-OAuth, formless definition (Postgres/BigQuery/Snowflake sources ship no ui-config)
 * resolves to `'external'` and has nothing to render, so it must not offer Edit either. Delete is
 * Delete is offered IFF the caller passes `onDelete`, and admin-gated when it is. Absence is the
 * mechanism, not a second flag: this took an `onDelete` plus a `hideDelete` boolean until
 * 2026-09-18, which meant the two wizard surfaces each shipped a handler that could only throw,
 * wrapped in a paragraph explaining why it was unreachable — and the type still let a caller pass a
 * real handler with `hideDelete` (dead code, no warning) or omit the flag with a handler (Delete
 * silently appearing in a wizard kebab). One knob cannot disagree with itself. `SettingsAccounts`
 * passes a handler because it owns an account's lifecycle; the wizard surfaces do not, because
 * deleting the credential a sync is mid-way through choosing — or actively delivering with — is not
 * a thing anyone means to do there.
 *
 * Its list gates on the ACCOUNTS query alone, so rows paint before `listDefinitions`
 * settles and `definition` is briefly undefined for every row. Omitting Edit there made it appear
 * under the user's cursor a moment after they opened the kebab. `definitionsPending` tells that
 * window apart from a settled "there is no definition" (a definitions failure, or a definition the
 * platform does not return): the first renders Edit disabled with a reason, per the repo's
 * never-hide / no-dead-affordance rule; the second still omits it, because there is genuinely no
 * form to open.
 *
 * Edit's OAuth exclusion is the PLATFORM's rule, not a UI preference, and it covers a rename as well
 * as a credential change — which is the part worth writing down, because "let me at least rename it"
 * is the obvious next question and the answer is no. Probed directly against config-backend
 * (2026-09-18), bypassing Lookout's own guard, with a name-only body carrying no options and no
 * secrets: `PATCH /web/workspaces/:id/accounts/:id {"name": "…"}` answers
 * `400 {"message":"Editing of Oauth-based account is currently not supported"}`. So narrowing
 * `updateRsAccount`'s blanket refusal to allow name-only edits would not unlock renaming; it would
 * move the same 400 from our layer to theirs and turn a clean absence into a platform error. If that
 * "currently" ever changes, this is the gate to reopen — and the server guard with it.
 *
 * Edit is therefore HIDDEN rather than disabled-with-a-reason, the same call `reauthorizeVisibility`
 * makes below: there is nothing the viewer could do about it, which is what separates this from a
 * permission block.
 *
 * Re-authorize sits between them (2026-09-10), and Edit and Re-authorize are mutually exclusive by
 * construction: Edit is non-OAuth only, Re-authorize is OAuth only, so no row can show both. Its
 * three states are `reauthorizeVisibility`'s, unchanged — `hidden` omits the item, `pending` renders
 * it disabled with `DEFINITIONS_LOADING_REASON` (same never-hide reasoning as Edit), `ready` offers
 * it. `onReauthorize` only OPENS the confirm dialog; it must never start the OAuth flow itself — see
 * `ReauthorizeAction.confirmAndStart` for the gesture rule that governs that.
 */
export function buildAccountRowActions<TRow extends AccountRowActionSubject>(
  row: TRow,
  definition: AccountRowActionDefinition | undefined,
  opts: {
    isAdmin: boolean;
    onEdit: (row: TRow) => void;
    /** Omit to leave Delete out of this surface's kebab entirely. */
    onDelete?: (row: TRow) => void;
    /**
     * Open the re-authorize CONFIRM DIALOG for this row — never `oauth.start`. Required rather than
     * optional so a surface cannot silently drop the item from its kebab.
     */
    onReauthorize: (row: TRow) => void;
    /** `listDefinitions` has not settled, so an undefined `definition` means "not yet", not "never". */
    definitionsPending?: boolean;
    /**
     * Why a viewer who may not write accounts cannot. Defaults to {@link ADMIN_ONLY_REASON}; a sibling workspace
     * passes the RudderStack grant the viewer lacks (ACT2-1124 LLD §6).
     */
    deniedReason?: string;
  },
): KebabMenuAction[] {
  const actions: KebabMenuAction[] = [];
  // OAuth rows are re-authorized, never edited — they get no Edit in either state.
  const editable = row.authenticationType !== 'oauth';
  const awaitingDefinition = editable && definition === undefined && opts.definitionsPending === true;
  if (editable && (definition?.lookoutSupport === 'form' || awaitingDefinition)) {
    actions.push({
      label: 'Edit',
      icon: Pencil,
      disabled: awaitingDefinition || !opts.isAdmin,
      disabledReason: awaitingDefinition ? DEFINITIONS_LOADING_REASON : (opts.deniedReason ?? ADMIN_ONLY_REASON),
      onSelect: () => opts.onEdit(row),
    });
  }
  const reauthorize = reauthorizeVisibility(row, definition, opts.definitionsPending === true);
  if (reauthorize !== 'hidden') {
    actions.push({
      label: 'Re-authorize',
      icon: RefreshCw,
      disabled: reauthorize === 'pending' || !opts.isAdmin,
      disabledReason: reauthorize === 'pending' ? DEFINITIONS_LOADING_REASON : (opts.deniedReason ?? ADMIN_ONLY_REASON),
      onSelect: () => opts.onReauthorize(row),
    });
  }
  const onDelete = opts.onDelete;
  if (onDelete) {
    actions.push({
      label: 'Delete',
      icon: Trash2,
      destructive: true,
      disabled: !opts.isAdmin,
      disabledReason: opts.deniedReason ?? ADMIN_ONLY_REASON,
      onSelect: () => onDelete(row),
    });
  }
  return actions;
}

/**
 * Whether a row's "Re-authorize" control can be offered, and in what state (Leo #7).
 *
 * `authenticationType === 'oauth'` alone is not enough: an OAuth definition Lookout cannot RELAY
 * resolves to `lookoutSupport` `'external'` (it is not on the OAuth allowlist), and the server refuses
 * the relay for it — so the item would open a confirm dialog, then a popup, only to come back "Connect
 * this account in RudderStack". That is a dead affordance, and unlike a permission block there is
 * nothing the viewer could do about it here, so it is hidden rather than disabled.
 *
 * The loading window gets the same treatment Edit does: `'pending'` means the definition simply has
 * not arrived, so the item renders disabled-with-a-reason instead of appearing a moment later — under
 * an already-open kebab, that would put a credential-replacing action beneath the user's cursor.
 */
export function reauthorizeVisibility(
  row: Pick<AccountRowActionSubject, 'authenticationType'>,
  definition: AccountRowActionDefinition | undefined,
  definitionsPending: boolean,
): 'hidden' | 'pending' | 'ready' {
  if (row.authenticationType !== 'oauth') return 'hidden';
  if (definition?.lookoutSupport === 'oauth') return 'ready';
  return definition === undefined && definitionsPending ? 'pending' : 'hidden';
}
